/**
 * useAuraChat Hook
 *
 * Handles streaming chat for Aura HTML generation.
 * Parses the response in real-time to separate explanation from HTML.
 */

import { useState, useCallback, useRef } from "react"
import { useAuth } from "../../../lib/auth-context"
import { trpc } from "../../../lib/trpc"

// Throttle helper - only call fn at most once per delay ms
function throttle<T extends (...args: any[]) => void>(fn: T, delay: number): T {
  let lastCall = 0
  let timeoutId: ReturnType<typeof setTimeout> | null = null

  return ((...args: Parameters<T>) => {
    const now = Date.now()
    const timeSinceLastCall = now - lastCall

    if (timeSinceLastCall >= delay) {
      lastCall = now
      fn(...args)
    } else if (!timeoutId) {
      // Schedule a call for the end of the delay period
      timeoutId = setTimeout(() => {
        lastCall = Date.now()
        timeoutId = null
        fn(...args)
      }, delay - timeSinceLastCall)
    }
  }) as T
}

// ============================================================================
// TYPES
// ============================================================================

export type GenerationMode = "CREATE" | "EDIT"

export interface GenerationMetrics {
  tokensInput?: number
  tokensOutput?: number
  durationMs?: number
  modelId?: string
}

interface AuraMessage {
  id: string
  role: "user" | "assistant"
  content: string
  // Link to version created by this message
  created_version_id?: string | null
  // DEPRECATED - kept for backwards compatibility during migration
  generated_html?: string | null
  version_number?: number | null
  is_applied?: boolean
  // AI metadata
  model_id?: string | null
  tokens_input?: number | null
  tokens_output?: number | null
  generation_duration_ms?: number | null
  created_at: Date | string
}

interface UseAuraChatOptions {
  conversationId: string
  onHtmlUpdate?: (html: string) => void
  onVersionComplete?: (versionNumber: number, html: string, messageId: string, metrics?: GenerationMetrics) => void
  onError?: (error: string) => void
}

interface UseAuraChatReturn {
  messages: AuraMessage[]
  sendMessage: (
    content: string,
    pageContext?: string,
    modelId?: string,
    mode?: GenerationMode,
    styleReference?: { url?: string; imageUrl?: string },
  ) => Promise<void>
  isLoading: boolean
  isStreaming: boolean
  streamingExplanation: string
  streamingHtml: string
  error: string | null
  refetch: () => void
  lastMetrics: GenerationMetrics | null
}

// ============================================================================
// STREAM PARSER
// ============================================================================

/**
 * Parses streamed content to extract explanation and HTML in real-time
 */
function parseStreamContent(content: string): {
  explanation: string
  html: string
  isInHtmlSection: boolean
} {
  const htmlStartMarker = "---HTML_START---"
  const htmlEndMarker = "---HTML_END---"

  const htmlStart = content.indexOf(htmlStartMarker)
  const htmlEnd = content.indexOf(htmlEndMarker)

  if (htmlStart === -1) {
    return {
      explanation: content.trim(),
      html: "",
      isInHtmlSection: false,
    }
  }

  const explanation = content.slice(0, htmlStart).trim()
  const html = content.slice(htmlStart + htmlStartMarker.length, htmlEnd > -1 ? htmlEnd : undefined).trim()

  return {
    explanation,
    html,
    isInHtmlSection: htmlEnd === -1, // Still streaming HTML if no end marker
  }
}

// ============================================================================
// HOOK
// ============================================================================

export function useAuraChat({
  conversationId,
  onHtmlUpdate,
  onVersionComplete,
  onError,
}: UseAuraChatOptions): UseAuraChatReturn {
  const { session } = useAuth()
  const utils = trpc.useUtils()

  // State
  const [isLoading, setIsLoading] = useState(false)
  const [isStreaming, setIsStreaming] = useState(false)
  const [streamingExplanation, setStreamingExplanation] = useState("")
  const [streamingHtml, setStreamingHtml] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [lastMetrics, setLastMetrics] = useState<GenerationMetrics | null>(null)

  // Refs for cleanup
  const abortControllerRef = useRef<AbortController | null>(null)

  // Fetch messages from tRPC
  const { data: messagesData, refetch } = trpc.aura.listMessages.useQuery(
    { conversationId, limit: 50 },
    { enabled: !!conversationId },
  )

  const messages = (messagesData ?? []) as AuraMessage[]

  /**
   * Send a message and stream the response
   * @param content - The user's message
   * @param pageContext - Current HTML for context (optional)
   * @param modelId - Model to use for generation
   * @param mode - Generation mode: CREATE (new page) or EDIT (modify existing)
   * @param styleReference - Optional style reference (URL or image URL)
   */
  const sendMessage = useCallback(
    async (
      content: string,
      pageContext?: string,
      modelId?: string,
      mode: GenerationMode = "CREATE",
      styleReference?: { url?: string; imageUrl?: string },
    ) => {
      console.log("[useAuraChat] sendMessage called", {
        content: content.substring(0, 100),
        hasPageContext: !!pageContext,
        modelId,
        mode,
        styleReference,
        hasStyleReference: !!styleReference,
      })

      if (!session?.access_token) {
        setError("Not authenticated")
        onError?.("Not authenticated")
        return
      }

      // Abort any existing stream
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }

      const abortController = new AbortController()
      abortControllerRef.current = abortController

      setIsLoading(true)
      setIsStreaming(true)
      setStreamingExplanation("")
      setStreamingHtml("")
      setError(null)

      // Create throttled update functions for performance
      // Only update preview every 150ms to prevent iframe thrashing
      const throttledHtmlUpdate = onHtmlUpdate ? throttle((html: string) => onHtmlUpdate(html), 150) : null
      const throttledSetStreamingHtml = throttle(setStreamingHtml, 100)

      try {
        // TEMP: Force localhost for development - TODO: use env var
        const apiUrl = "http://localhost:8790"
        // const apiUrl = import.meta.env.VITE_API_URL || "http://localhost:8790"

        const requestBody = {
          conversationId,
          content,
          pageContext,
          modelId,
          mode,
          styleReference,
        }
        console.log("[useAuraChat] Sending request to API", {
          url: `${apiUrl}/api/aura-chat/stream`,
          bodyKeys: Object.keys(requestBody),
          hasStyleReference: !!styleReference,
          styleReference,
        })

        const response = await fetch(`${apiUrl}/api/aura-chat/stream`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify(requestBody),
          signal: abortController.signal,
        })

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}))
          throw new Error(errorData.error || `HTTP error ${response.status}`)
        }

        // Process SSE stream
        const reader = response.body?.getReader()
        if (!reader) {
          throw new Error("No response body")
        }

        const decoder = new TextDecoder()
        let fullContent = ""
        let buffer = ""
        let lastHtml = ""

        // eslint-disable-next-line no-constant-condition -- streaming loop exits via break when done
        while (true) {
          const { done, value } = await reader.read()

          if (done) {
            break
          }

          const chunk = decoder.decode(value, { stream: true })
          buffer += chunk

          // Process complete SSE events
          const lines = buffer.split("\n")
          buffer = lines.pop() || ""

          for (const line of lines) {
            if (line.startsWith("data: ")) {
              const data = line.slice(6)

              if (data === "[DONE]") {
                // Final update - ensure we have the complete HTML
                if (lastHtml && onHtmlUpdate) {
                  onHtmlUpdate(lastHtml)
                }
                setIsStreaming(false)
                continue
              }

              try {
                const parsed = JSON.parse(data)

                // Check if this is the final metadata event
                if (parsed.done && parsed.versionNumber) {
                  const { html } = parseStreamContent(fullContent)
                  // Final update - direct call, not throttled
                  if (html && onHtmlUpdate) {
                    onHtmlUpdate(html)
                  }

                  // Capture and store metrics
                  const metrics: GenerationMetrics | undefined = parsed.metrics
                    ? {
                        tokensInput: parsed.metrics.tokensInput,
                        tokensOutput: parsed.metrics.tokensOutput,
                        durationMs: parsed.metrics.durationMs,
                        modelId: parsed.metrics.modelId,
                      }
                    : undefined

                  if (metrics) {
                    setLastMetrics(metrics)
                  }

                  onVersionComplete?.(parsed.versionNumber, html, parsed.messageId, metrics)
                  continue
                }

                // Regular text chunk
                if (typeof parsed === "string") {
                  fullContent += parsed

                  // Parse content
                  const { explanation, html } = parseStreamContent(fullContent)

                  // Always update explanation immediately (it's short)
                  setStreamingExplanation(explanation)

                  // Throttle HTML updates for performance
                  if (html !== lastHtml) {
                    lastHtml = html
                    throttledSetStreamingHtml(html)
                    throttledHtmlUpdate?.(html)
                  }
                }
              } catch {
                // Ignore parse errors for malformed chunks
              }
            }
          }
        }

        // Refresh messages from server
        await refetch()
        void utils.aura.listMessages.invalidate({ conversationId })
      } catch (err: any) {
        if (err.name === "AbortError") {
          return
        }

        const errorMessage = err.message || "Failed to send message"
        setError(errorMessage)
        onError?.(errorMessage)
      } finally {
        setIsLoading(false)
        setIsStreaming(false)
        abortControllerRef.current = null
      }
    },
    [session?.access_token, conversationId, onHtmlUpdate, onVersionComplete, onError, refetch, utils],
  )

  return {
    messages,
    sendMessage,
    isLoading,
    isStreaming,
    streamingExplanation,
    streamingHtml,
    error,
    refetch: () => {
      void refetch()
    },
    lastMetrics,
  }
}
